import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";

export default function ZonasDistribucion() {
  const [zonas, setZonas] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editando, setEditando] = useState(false);
  const [zonaActual, setZonaActual] = useState({
    nombre: "",
    descripcion: "",
    codigo_postal: "",
    distritos: "",
    radio_km: "",
    costo_base: "",
    tiempo_promedio: "",
    conductor_asignado: "",
    estado: "activa",
  });

  useEffect(() => {
    cargarZonas();
  }, []);

  const cargarZonas = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.LOGISTICA.ZONAS);
      setZonas(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al cargar zonas:", error);
      setZonas([]);
    }
  };

  const handleOpenDialog = (zona = null) => {
    if (zona) {
      setEditando(true);
      setZonaActual({
        nombre: zona.nombre,
        descripcion: "",
        codigo_postal: zona.codigo,
        distritos: zona.distritos,
        radio_km: zona.radio,
        costo_base: zona.costo_base,
        tiempo_promedio: zona.tiempo_promedio,
        conductor_asignado: "",
        estado: zona.estado,
      });
    } else {
      setEditando(false);
      setZonaActual({
        nombre: "",
        descripcion: "",
        codigo_postal: "",
        distritos: "",
        radio_km: "",
        costo_base: "",
        tiempo_promedio: "",
        conductor_asignado: "",
        estado: "activa",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditando(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setZonaActual({ ...zonaActual, [name]: value });
  };

  const handleGuardar = async () => {
    try {
      if (editando) {
        await axios.put(`${API_ENDPOINTS.LOGISTICA.ZONAS}/${zonaActual.id}`, zonaActual);
      } else {
        await axios.post(API_ENDPOINTS.LOGISTICA.ZONAS, zonaActual);
      }
      handleCloseDialog();
      cargarZonas();
    } catch (error) {
      console.error("Error al guardar zona:", error);
    }
  };

  const handleEliminar = async (id) => {
    if (window.confirm("¿Está seguro de eliminar esta zona?")) {
      try {
        await axios.delete(`${API_ENDPOINTS.LOGISTICA.ZONAS}/${id}`);
        cargarZonas();
      } catch (error) {
        console.error("Error al eliminar zona:", error);
      }
    }
  };

  const getEstadoChip = (estado) => {
    return estado === "activa" ? (
      <Chip label="Activa" color="success" size="small" />
    ) : (
      <Chip label="Inactiva" color="secondary" size="small" />
    );
  };

  /* eslint-disable react/prop-types */
  const columns = [
    { Header: "Código", accessor: "codigo", width: "10%" },
    { Header: "Nombre", accessor: "nombre", width: "15%" },
    { Header: "Distritos", accessor: "distritos", width: "25%" },
    {
      Header: "Radio",
      accessor: "radio",
      width: "8%",
      Cell: ({ value }) => `${value} km`,
    },
    {
      Header: "Costo Base",
      accessor: "costo_base",
      width: "10%",
      Cell: ({ value }) => `$${(value || 0).toFixed(2)}`,
    },
    { Header: "Tiempo Prom.", accessor: "tiempo_promedio", width: "10%" },
    {
      Header: "Rutas",
      accessor: "rutas_activas",
      width: "8%",
      Cell: ({ value }) => (
        <MDBox textAlign="center">
          <Chip label={value} color="info" size="small" />
        </MDBox>
      ),
    },
    {
      Header: "Entregas/Mes",
      accessor: "entregas_mes",
      width: "10%",
      Cell: ({ value }) => (
        <MDTypography variant="button" fontWeight="medium">
          {value}
        </MDTypography>
      ),
    },
    {
      Header: "Acciones",
      accessor: "acciones",
      width: "8%",
      Cell: ({ row }) => (
        <MDBox display="flex" gap={1}>
          <Tooltip title="Editar">
            <IconButton size="small" color="info" onClick={() => handleOpenDialog(row.original)}>
              <Icon fontSize="small">edit</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Ver Mapa">
            <IconButton size="small" color="primary">
              <Icon fontSize="small">map</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" color="error" onClick={() => handleEliminar(row.original.id)}>
              <Icon fontSize="small">delete</Icon>
            </IconButton>
          </Tooltip>
        </MDBox>
      ),
    },
  ];
  /* eslint-enable react/prop-types */

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <MDTypography variant="h5">Zonas de Distribución</MDTypography>
        <MDButton
          variant="gradient"
          color="info"
          startIcon={<Icon>add</Icon>}
          onClick={() => handleOpenDialog()}
        >
          Nueva Zona
        </MDButton>
      </MDBox>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2} textAlign="center">
              <Icon fontSize="large" color="info">
                map
              </Icon>
              <MDTypography variant="h4" fontWeight="bold" mt={1}>
                {zonas.length}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Zonas Totales
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2} textAlign="center">
              <Icon fontSize="large" color="success">
                check_circle
              </Icon>
              <MDTypography variant="h4" fontWeight="bold" mt={1}>
                {zonas.filter((z) => z.estado === "activa").length}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Zonas Activas
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2} textAlign="center">
              <Icon fontSize="large" color="primary">
                route
              </Icon>
              <MDTypography variant="h4" fontWeight="bold" mt={1}>
                {zonas.reduce((sum, z) => sum + z.rutas_activas, 0)}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Rutas Activas
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2} textAlign="center">
              <Icon fontSize="large" color="warning">
                local_shipping
              </Icon>
              <MDTypography variant="h4" fontWeight="bold" mt={1}>
                {zonas.reduce((sum, z) => sum + z.entregas_mes, 0)}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Entregas este Mes
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <DataTable
          table={{ columns, rows: zonas }}
          showTotalEntries={true}
          isSorted={true}
          noEndBorder
          entriesPerPage={false}
          canSearch
        />
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editando ? "Editar Zona de Distribución" : "Nueva Zona de Distribución"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombre de la Zona"
                name="nombre"
                value={zonaActual.nombre}
                onChange={handleInputChange}
                required
                placeholder="Ej: Zona Norte"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Código Postal"
                name="codigo_postal"
                value={zonaActual.codigo_postal}
                onChange={handleInputChange}
                placeholder="ZN-01"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Distritos Incluidos"
                name="distritos"
                value={zonaActual.distritos}
                onChange={handleInputChange}
                placeholder="Los Olivos, Independencia, Comas"
                helperText="Separar con comas"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Descripción"
                name="descripcion"
                value={zonaActual.descripcion}
                onChange={handleInputChange}
                placeholder="Descripción de la zona..."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Radio (km)"
                name="radio_km"
                value={zonaActual.radio_km}
                onChange={handleInputChange}
                inputProps={{ min: 0, step: 0.1 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Costo Base"
                name="costo_base"
                value={zonaActual.costo_base}
                onChange={handleInputChange}
                inputProps={{ min: 0, step: 0.5 }}
                InputProps={{
                  startAdornment: <span style={{ marginRight: 8 }}>$</span>,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tiempo Promedio de Entrega"
                name="tiempo_promedio"
                value={zonaActual.tiempo_promedio}
                onChange={handleInputChange}
                placeholder="45 min"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Estado"
                name="estado"
                value={zonaActual.estado}
                onChange={handleInputChange}
                select
              >
                <MenuItem value="activa">Activa</MenuItem>
                <MenuItem value="inactiva">Inactiva</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleGuardar} variant="contained" color="info">
            {editando ? "Actualizar" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
